import React, { useState, useEffect, useCallback } from 'react';
import { PackagePlus, ArrowLeft, FilePlus2, List, Settings2, Plus, Database } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { getFieldConfig, getMaterial } from './materialApi';
import MaterialCaptureForm from './MaterialCaptureForm';
import MaterialFieldConfig from './MaterialFieldConfig';
import MaterialList from './MaterialList';
import SapMaterialSearchDialog from './SapMaterialSearchDialog';

/**
 * MaterialErfassungView — Modul „Material-Schnellerfassung" (Owner: Taras).
 *
 * MM01/F1604-Light: schlanke, admin-konfigurierbare Materialanlage. Drei Bereiche:
 *   - Erfassen          dynamische Maske (nur aktive Felder, Pflichtkern fix)
 *   - Übersicht         angelegte Materialien (+ Vorlage/Spiegeln/Löschen)
 *   - Feldkonfiguration nur Admin — entscheidet, welche Felder aktiv/Pflicht sind
 *
 * Props (App.js renderCurrentView): user, onBack, showSuccess, showError
 */
const MaterialErfassungView = ({ user, onBack, showSuccess, showError }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const isAdmin = user?.role === 'admin';

  const [tab, setTab] = useState('capture'); // capture | list | config
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // Material als initial fuer die Maske
  const [refreshKey, setRefreshKey] = useState(0);
  const [sapSearchOpen, setSapSearchOpen] = useState(false);

  // SAP-Material als Vorlage uebernehmen -> Erfassen mit vorbefuellten Feldern
  const handleSapPick = (tpl) => { setEditing(tpl); setTab('capture'); setSapSearchOpen(false); };

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getFieldConfig(user);
      setConfig(data);
    } catch (e) {
      showError?.(t('material.configLoadError'));
    } finally {
      setLoading(false);
    }
  }, [user, showError, t]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const handleSaved = () => {
    setEditing(null);
    setRefreshKey((k) => k + 1);
    setTab('list');
  };

  // Nach dem Anlegen: Material in den Bearbeiten-Modus laden und im Erfassen-Tab
  // bleiben (so sind PDF-Anhänge sofort möglich). Fallback: zur Übersicht.
  const handleCreated = async (id) => {
    setRefreshKey((k) => k + 1);
    if (!id) { handleSaved(); return; }
    try {
      const { material } = await getMaterial(user, id);
      setEditing(material);
      setTab('capture');
    } catch {
      handleSaved();
    }
  };

  const handleEdit = (material) => { setEditing(material); setTab('capture'); };
  const handleTemplate = (templateData) => { setEditing(templateData); setTab('capture'); };
  const handleNew = () => { setEditing(null); setTab('capture'); };

  // -- Styles (an LagerShell/Scaffold angelehnt) ---------------------------
  const bgCls = isDarkMode
    ? 'bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800'
    : 'bg-[#F8FAFC]';
  const headerBg = isDarkMode
    ? 'bg-gray-900/20 border-b border-white/10 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.3)]'
    : 'bg-white border-b border-slate-200';
  const backBtnCls = isDarkMode
    ? 'flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-white/10 hover:bg-white/15 text-white/80 hover:text-white border border-white/10'
    : 'flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900';

  const tabs = [
    { key: 'capture', labelKey: 'material.tabCapture', Icon: FilePlus2 },
    { key: 'list',    labelKey: 'material.tabList',    Icon: List },
    ...(isAdmin ? [{ key: 'config', labelKey: 'material.tabConfig', Icon: Settings2 }] : []),
  ];

  const tabBtn = (tb) => {
    const active = tab === tb.key;
    return (
      <button key={tb.key} type="button" onClick={() => setTab(tb.key)}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
          active
            ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/20'
            : isDarkMode ? 'text-white/60 hover:text-white hover:bg-white/10' : 'text-slate-600 hover:bg-slate-100'
        }`}>
        <tb.Icon className="w-4 h-4" /> {t(tb.labelKey)}
      </button>
    );
  };

  return (
    <div className={`flex-1 min-h-screen ${bgCls}`}>
      {/* Header */}
      <div className="relative">
        <div className={`absolute inset-0 ${headerBg}`} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className={backBtnCls}>
              <ArrowLeft className="w-4 h-4" /> {t('common.back')}
            </button>
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                isDarkMode ? 'bg-gradient-to-br from-emerald-500/70 to-emerald-700/70 border border-white/20' : 'bg-emerald-100'
              }`}>
                <PackagePlus className={`w-6 h-6 ${isDarkMode ? 'text-white/90' : 'text-emerald-600'}`} />
              </div>
              <div className="min-w-0">
                <h1 className={`text-xl font-bold truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('material.title')}</h1>
                <p className={`text-xs mt-0.5 truncate ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>{t('material.subtitle')}</p>
              </div>
            </div>
          </div>

          {/* Tabs + Aktionen */}
          <div className="flex items-center justify-between gap-3 mt-4">
            <div className="flex items-center gap-2 flex-wrap">{tabs.map(tabBtn)}</div>
            <div className="flex items-center gap-2">
              {tab !== 'config' && (
                <button type="button" onClick={() => setSapSearchOpen(true)}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium ${
                    isDarkMode ? 'bg-blue-500/15 hover:bg-blue-500/25 text-blue-200 border border-blue-400/20' : 'bg-blue-50 hover:bg-blue-100 text-blue-700'
                  }`}>
                  <Database className="w-4 h-4" /> {t('material.sapLoad')}
                </button>
              )}
              {tab === 'capture' && editing && (
                <button type="button" onClick={handleNew}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium ${
                    isDarkMode ? 'bg-white/10 hover:bg-white/15 text-white/80' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                  }`}>
                  <Plus className="w-4 h-4" /> {t('material.newMaterial')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Inhalt */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading && !config ? ( /* nur beim Erstladen; ein Reload darf Liste/Dialoge nicht unmounten */
          <div className={`p-12 text-center text-sm ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`}>{t('common.loading')}</div>
        ) : tab === 'capture' ? (
          <MaterialCaptureForm key={editing?.id || 'new'} user={user} config={config} initial={editing}
            onSaved={handleSaved} onCreated={handleCreated} showSuccess={showSuccess} showError={showError} />
        ) : tab === 'list' ? (
          <MaterialList user={user} onEdit={handleEdit} onUseAsTemplate={handleTemplate}
            showSuccess={showSuccess} showError={showError} refreshKey={refreshKey} />
        ) : (
          <MaterialFieldConfig user={user} config={config}
            onSaved={(fresh) => setConfig(fresh)} showSuccess={showSuccess} showError={showError} />
        )}
      </div>

      {sapSearchOpen && (
        <SapMaterialSearchDialog user={user} config={config} onPick={handleSapPick}
          onClose={() => setSapSearchOpen(false)} showError={showError} />
      )}
    </div>
  );
};

export default MaterialErfassungView;
